import React, { useState } from 'react';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

import TruncateMarkup from 'react-truncate-markup';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import type { Company, UserProfileQuery } from 'src/types/__generated__/graphql';

import RenderLineBreaks from 'src/components/ui/RenderLineBreaks';
import LinkButton from 'src/components/ui/LinkButton';
import Typography from 'src/components/ui/Typography';
import { Tabs, Tab } from 'src/components/ui/Tabs';

import formatDistanceToNow from 'src/utils/formatDistanceToNow';
import CompanyDetailsBlock from 'src/components/company/CompanyDetailsBlock';
import { styledComponent } from 'src/utils/styled';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { AnalyticsEvent } from 'src/utils/analytics';
import useBreakpoints from 'src/hooks/useBreakpoints';

const StyledCardContent = styledComponent(CardContent)`
    position: relative;
    padding: 1.5rem 0;
`;

type TabTypes = 'description' | 'company';

export interface Props {
    userProfile: NonNullable<UserProfileQuery['userProfile']>;
}

const StyledTypography = styledComponent(Typography)<{ component?: string }>``;

const AboutSection: React.FC<Props> = ({ userProfile }) => {
    const { t, i18n } = useTranslation(['profile']);
    const { logEventDynamic } = useDynamicLogEvents();

    const [textExpanded, setTextExpanded] = useState(false);
    const [selectedTab, setSelectedTab] = useState<TabTypes>('description');

    const { isMobile } = useBreakpoints();

    const handleTabChange = (_ev: React.SyntheticEvent, tab: TabTypes) => {
        let analyticsEvent;
        if (tab === 'description') {
            analyticsEvent = AnalyticsEvent.AboutDescription;
        } else {
            analyticsEvent = AnalyticsEvent.AboutCompany;
        }
        logEventDynamic(analyticsEvent);
        setSelectedTab(tab);
    };

    const toggleExpand = () => {
        setTextExpanded(!textExpanded);
    };
    let content;
    if (userProfile.description && textExpanded) {
        content = (
            <>
                <StyledTypography component="span">
                    <RenderLineBreaks>{userProfile.description}</RenderLineBreaks>
                </StyledTypography>
                <Box my={2}>
                    <LinkButton as="span" onClick={toggleExpand}>
                        {t('profile:readLess')}
                    </LinkButton>
                </Box>
            </>
        );
    } else if (userProfile.description) {
        content = (
            <StyledTypography component="span">
                <RenderLineBreaks>
                    <TruncateMarkup
                        lines={8}
                        ellipsis={
                            <>
                                ...
                                <Box my={2}>
                                    <LinkButton onClick={toggleExpand}>{t('profile:readMore')}</LinkButton>
                                </Box>
                            </>
                        }
                    >
                        <span>{userProfile.description}</span>
                    </TruncateMarkup>
                </RenderLineBreaks>
            </StyledTypography>
        );
    } else {
        content = <Typography>{t('profile:noDescription')}</Typography>;
    }
    return (
        <Card elevation={0}>
            <StyledCardContent>
                <Typography variant="h2" serif>
                    {t('profile:about')}
                </Typography>
                <Box display="flex" alignItems="center" my={2}>
                    <Tabs value={selectedTab} onChange={handleTabChange}>
                        <Tab
                            label={t('profile:description')}
                            value="description"
                            disableRipple={!userProfile.isProfessionnal}
                            style={!userProfile.isProfessionnal ? { cursor: 'default' } : {}}
                        />
                        {userProfile.isProfessionnal && <Tab label={t('common:company')} value="company" />}
                    </Tabs>
                </Box>
                {selectedTab === 'description' && (
                    <Box>
                        <Box
                            display="flex"
                            flexDirection={isMobile ? 'column' : 'row'}
                            alignItems={isMobile ? 'flex-start' : 'center'}
                            my={1}
                        >
                            <Box my={1}>
                                <StyledTypography
                                    component="span"
                                    color="dark"
                                    dangerouslySetInnerHTML={{
                                        __html: t('profile:registeredSince', {
                                            highlight: formatDistanceToNow(
                                                userProfile.createdAt as string,
                                                i18n.language as SupportedLangs,
                                            ),
                                        }),
                                    }}
                                />
                            </Box>
                        </Box>
                        {content}
                    </Box>
                )}
                {selectedTab === 'company' && (Boolean(userProfile.companyToDisplay) || userProfile.phoneNumber) && (
                    <CompanyDetailsBlock user={userProfile} company={userProfile.companyToDisplay as Company} />
                )}
            </StyledCardContent>
        </Card>
    );
};

export default AboutSection;
